import { FC, memo, useMemo } from "react"
import classNames from "classnames"
import { useSelector } from "react-redux"
import NumberBadge, { NumberBadgeVariant } from "../NumberBadge"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { Icon, IconColor } from "@/components/shared/Icon"
import { TestId } from "@/constants/globals"
import { useFastAlarmClearanceEnabled } from "@/hooks/useFastAlarmClearanceEnabled"
import { useTruncation } from "@/hooks/useTruncation"
import { selectTimezone } from "@/store/facilitiesSlice"
import { AlertVariant, AlertVitals, TypefaceVariant } from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { ButtonVariant } from "@/types/index"
import { getAlertSystemConditionTypeIconName } from "@/utils/alertConfiguration"
import { relationalTime } from "@/utils/alerts"
import { renderWithTooltip } from "@/utils/index"
import { isEqual } from "@/utils/lodash"
import "./index.scss"

export interface AlertWearableCardProps {
   // cellId: string
   inmateName: string
   locationName?: string
   vitals?: AlertVitals
   priority: number
   systemConditionType?: AlertSystemConditionType
   alertVariant?: keyof typeof AlertVariant
   typefaceColor: keyof typeof TypefaceVariant
   dateTime: string
   // chargeLevel?: number
   onClick?: () => void
   onClearAlert?: () => void
   onSwapDevice?: () => void
}

const AlertWearableCard: FC<AlertWearableCardProps> = ({
   inmateName,
   locationName,
   vitals,
   priority,
   alertVariant,
   typefaceColor,
   dateTime,
   systemConditionType,
   onClick,
   onClearAlert = () => {},
   onSwapDevice = () => {},
}) => {
   const timezone = useSelector(selectTimezone)
   const informativeTime = useMemo(() => {
      return relationalTime(dateTime, timezone)
   }, [dateTime, timezone])

   const [inmateNameRef, isInmateIdTruncated] =
      useTruncation<HTMLSpanElement>(inmateName)
   const [informativeTimeRef, isInformativeTimeTruncated] =
      useTruncation<HTMLSpanElement>(informativeTime)
   const [locationNameRef, isLocationNameTruncated] =
      useTruncation<HTMLSpanElement>(locationName)

   const { lowHeartRate, highHeartRate, lowOxygenLevel } = vitals || {}

   const fastAlarmClearanceEnabled = useFastAlarmClearanceEnabled()

   const showClearButton =
      fastAlarmClearanceEnabled &&
      (lowHeartRate ||
         highHeartRate ||
         lowOxygenLevel ||
         systemConditionType === AlertSystemConditionType.OUT_OF_BOUNDS ||
         systemConditionType === AlertSystemConditionType.NO_VITALS)

   const getAlertColor = () => {
      return `--${AlertVariant[alertVariant as keyof typeof AlertVariant]}`
   }

   return (
      <div
         className={classNames(
            "alert-wearable-card",
            `alert-wearable-card${getAlertColor()}`
         )}
         onClick={onClick}
         data-testid={TestId.WEARABLE_ALERT_CARD}
      >
         <div className="alert-wearable-card__content">
            <div className="alert-wearable-card__header">
               <div className="alert-wearable-card__title">
                  <NumberBadge
                     size={NumberBadgeVariant.SMALL}
                     number={priority}
                     typefaceColor={typefaceColor}
                  />
                  <span className="alert-wearable-card__smartwatch-icon">
                     <Icon
                        name="smartwatch"
                        size="16"
                        color={IconColor.WHITE}
                     />
                  </span>
                  {systemConditionType ===
                     AlertSystemConditionType.NO_VITALS && (
                     <span className="alert-wearable-card__status-text">
                        No Vitals
                     </span>
                  )}
                  {(lowHeartRate || highHeartRate) && (
                     <span>
                        <Icon
                           name="heartRate"
                           size="16"
                           color={IconColor.WHITE}
                        />
                        <Icon
                           name={
                              lowHeartRate ? "lowerThreshold" : "upperThreshold"
                           }
                           size="16"
                           color={IconColor.WHITE}
                        />
                     </span>
                  )}
                  {lowOxygenLevel && (
                     <span>
                        <Icon
                           name="oxygenLevel"
                           size="16"
                           color={IconColor.WHITE}
                        />
                        <Icon
                           name="lowerThreshold"
                           size="16"
                           color={IconColor.WHITE}
                        />
                     </span>
                  )}
                  {(systemConditionType ===
                     AlertSystemConditionType.OUT_OF_BOUNDS ||
                     systemConditionType ===
                        AlertSystemConditionType.BROKEN_WEARABLE) && (
                     <Icon
                        name={getAlertSystemConditionTypeIconName(
                           systemConditionType
                        )}
                        size="16"
                        color={IconColor.WHITE}
                     />
                  )}
               </div>
               <div className="alert-wearable-card__time">
                  {renderWithTooltip(
                     informativeTimeRef,
                     "alert-wearable-card__time-text",
                     informativeTime,
                     isInformativeTimeTruncated
                  )}
               </div>
            </div>
            <div className="alert-wearable-card__footer">
               <div className="alert-wearable-card__name-wrapper">
                  {renderWithTooltip(
                     inmateNameRef,
                     "alert-wearable-card__name",
                     inmateName,
                     isInmateIdTruncated
                  )}
               </div>
               <div className="alert-wearable-card__location-wrapper">
                  {renderWithTooltip(
                     locationNameRef,
                     "alert-wearable-card__location",
                     locationName,
                     isLocationNameTruncated
                  )}
               </div>
            </div>
         </div>
         <div className="alert-wearable-card__actions">
            {systemConditionType ===
            AlertSystemConditionType.BROKEN_WEARABLE ? (
               <Button
                  className="alert-wearable-card__action-button"
                  iconName="moveInmate"
                  iconSize="10"
                  iconPosition={ButtonIconPosition.LEFT}
                  variant={ButtonVariant.SMALL}
                  onClick={(e) => {
                     e.stopPropagation()
                     e.preventDefault()
                     onSwapDevice()
                  }}
               />
            ) : (
               showClearButton && (
                  <Button
                     className="alert-wearable-card__action-button"
                     iconName="clearAlerts"
                     iconSize="12"
                     iconPosition={ButtonIconPosition.LEFT}
                     variant={ButtonVariant.SMALL}
                     onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onClearAlert()
                     }}
                  />
               )
            )}
            <Icon
               className="alert-wearable-card__arrow-icon"
               name="arrowRight"
               size="12"
               color={IconColor.WHITE}
            />
         </div>
      </div>
   )
}

export default memo(AlertWearableCard, (prevProps, nextProps) => {
   const keys: (keyof AlertWearableCardProps)[] = [
      "inmateName",
      "vitals",
      "priority",
      "alertVariant",
      "typefaceColor",
      "systemConditionType",
      "dateTime",
      "onClick",
      "onClearAlert",
   ]
   return keys.every((key) => isEqual(prevProps[key], nextProps[key]))
})
